import React from 'react';
import {Box, Stack} from '@mui/material';
import SourcesMediaPlayer from './SourcesMediaPlayer';
import SourcesTranscriptView from './SourcesTranscriptView';
import DualLanguagePanes from './DualLanguagePanes';
import useMediaTranscriptSync from './useMediaTranscriptSync';
import useMediaSpacebarPlayPause from './useMediaSpacebarPlayPause';
import SourceFileDraftSelectionHost from './SourceFileDraftSelectionHost';

/**
 * Audio/video split-pane layout:
 *   ┌──────────────────────┐
 *   │  Player strip (top)  │  sticky (drag the divider to resize)
 *   ├──────────────────────┤  ← draggable divider
 *   │  Transcript (scroll) │
 *   └──────────────────────┘
 */

const MIN_TOP_PX = 44;
const MIN_BOTTOM_PX = 60;
const DEFAULT_TOP_PERCENT = 40;
// Audio-only player strip is a fixed compact height; video needs a resizable pane.
const AUDIO_PLAYER_HEIGHT_PX = 40;

/** Hook that returns the current height of a referenced container element. */
function useContainerHeight(ref) {
    const [height, setHeight] = React.useState(0);
    React.useEffect(() => {
        if (!ref.current) return;
        const ro = new ResizeObserver(([entry]) => {
            setHeight(entry.contentRect.height);
        });
        ro.observe(ref.current);
        setHeight(ref.current.getBoundingClientRect().height);
        return () => ro.disconnect();
    }, [ref]);
    return height;
}

const SourcesMediaTranscriptLayout = ({
    src,
    mimeType,
    fileType,
    pmContent,
    originalPmContent = null,
    originalLanguageLabel = 'Original',
    originalDir = 'ltr',
    isLoadingMedia,
    isLoadingTranscript,
    mediaError,
    transcriptError,
    processingStatus,
    seekToMs = null,
    onBlockHighlight = null,
    highlightedSegmentIds = null,
    highlightedRanges = null,
    linkContext = null,
    searchMatches = null,
    activeSearchMatchIdx = -1,
    draftContext = null,
    mediaDurationMs = null,
    posterUrl = null,
}) => {
    const isAudio = (
        mimeType?.startsWith('audio/') || mimeType === 'audio' || fileType === 'audio'
    ) && !(mimeType?.startsWith('video/') || mimeType === 'video' || fileType === 'video');
    const mediaRef = React.useRef(null);
    // English drives playback sync. The original PM inherits startMs/endMs 1:1
    // from the English/source paragraphs, so the same activeParagraphIndex and
    // seekTo apply to both columns.
    const {paragraphs, activeParagraphIndex, seekTo} = useMediaTranscriptSync({pmContent, mediaRef, src});
    const {paragraphs: originalParagraphs} = useMediaTranscriptSync({
        pmContent: originalPmContent,
        mediaRef,
    });
    const showOriginal = Array.isArray(originalPmContent) && originalParagraphs.length > 0;

    // M6 deep link: seek once to the search hit's media timestamp when the layout mounts
    // for that anchor. Re-arms whenever a different timestamp arrives (new hit opened).
    // Opening from a search result positions the media at the hit but does NOT start
    // playback — the user explicitly presses play (or clicks a transcript sentence).
    const seekedForMsRef = React.useRef(null);
    React.useEffect(() => {
        if (seekToMs == null) {
            seekedForMsRef.current = null;
            return;
        }
        if (seekedForMsRef.current === seekToMs) {
            return;
        }
        seekedForMsRef.current = seekToMs;
        seekTo(seekToMs, src, {autoplay: false});
    }, [seekToMs, src, seekTo]);

    const [topPercent, setTopPercent] = React.useState(DEFAULT_TOP_PERCENT);
    const containerRef = React.useRef(null);
    const isDragging = React.useRef(false);
    const containerHeight = useContainerHeight(containerRef);

    useMediaSpacebarPlayPause({
        mediaRef,
        containerRef,
        enabled: Boolean(src) && !mediaError,
        registerKey: `${src || ''}|${mimeType || ''}|${fileType || ''}`,
    });

    const topPx = isAudio
        ? AUDIO_PLAYER_HEIGHT_PX
        : containerHeight > 0
            ? Math.max(MIN_TOP_PX, Math.min(containerHeight - MIN_BOTTOM_PX, (topPercent / 100) * containerHeight))
            : null;

    const handleDividerMouseDown = React.useCallback((e) => {
        e.preventDefault();
        isDragging.current = true;
        document.body.style.cursor = 'row-resize';
        document.body.style.userSelect = 'none';

        const onMouseMove = (moveEvent) => {
            if (!isDragging.current || !containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const rawPx = moveEvent.clientY - rect.top;
            const clampedPx = Math.max(MIN_TOP_PX, Math.min(rect.height - MIN_BOTTOM_PX, rawPx));
            setTopPercent((clampedPx / rect.height) * 100);
        };

        const onMouseUp = () => {
            isDragging.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    }, []);

    return (
        <Stack ref={containerRef} sx={{flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative'}}>
            {/* Top panel — player */}
            <Box sx={{
                flexShrink: 0,
                overflow: 'hidden',
                ...(topPx !== null ? {height: topPx} : {flex: '0 0 40%'}),
            }}>
                <SourcesMediaPlayer
                    ref={mediaRef}
                    src={src}
                    mimeType={mimeType}
                    fileType={fileType}
                    isLoading={isLoadingMedia}
                    error={mediaError}
                    playerHeight={topPx}
                    initialDurationMs={mediaDurationMs}
                    posterUrl={posterUrl}
                />
            </Box>

            {/* Draggable divider — only meaningful for video; audio player is fixed height */}
            {!isAudio && (
                <Box
                    onMouseDown={handleDividerMouseDown}
                    sx={(theme) => ({
                        flexShrink: 0,
                        height: 5,
                        cursor: 'row-resize',
                        bgcolor: theme.palette.divider,
                        position: 'relative',
                        zIndex: 10,
                        transition: 'background-color 0.15s',
                        '&:hover': {
                            bgcolor: theme.palette.primary.main,
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 32,
                            height: 3,
                            borderRadius: 4,
                            bgcolor: 'rgba(255,255,255,0.55)',
                            pointerEvents: 'none',
                        },
                    })}
                />
            )}

            {/* Bottom panel — transcript(s). English left, original language right. */}
            <Box sx={{flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                <SourceFileDraftSelectionHost
                    draftContext={draftContext}
                    sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}
                >
                    <DualLanguagePanes
                        leftLabel="English"
                        rightLabel={originalLanguageLabel}
                        left={(
                            <SourcesTranscriptView
                                paragraphs={paragraphs}
                                activeParagraphIndex={activeParagraphIndex}
                                onSentenceClick={seekTo}
                                isLoading={isLoadingTranscript}
                                error={transcriptError}
                                processingStatus={processingStatus}
                                onBlockHighlight={onBlockHighlight}
                                highlightedSegmentIds={highlightedSegmentIds}
                                highlightedRanges={highlightedRanges}
                                linkContext={linkContext}
                                searchMatches={searchMatches}
                                activeSearchMatchIdx={activeSearchMatchIdx}
                            />
                        )}
                        right={showOriginal ? (
                            <SourcesTranscriptView
                                paragraphs={originalParagraphs}
                                activeParagraphIndex={activeParagraphIndex}
                                onSentenceClick={seekTo}
                                isLoading={isLoadingTranscript}
                                error={transcriptError}
                                processingStatus={processingStatus}
                                dir={originalDir}
                            />
                        ) : null}
                    />
                </SourceFileDraftSelectionHost>
            </Box>
        </Stack>
    );
};

export default SourcesMediaTranscriptLayout;
